import React, { useState, useEffect, useRef } from 'react';
import './InvoiceDatePicker.css'; // Use InvoiceDatePicker CSS for consistent styling

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M16 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 9H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 8C3 6.34315 4.34315 5 6 5H18C19.6569 5 21 6.34315 21 8V18C21 19.6569 19.6569 21 18 21H6C4.34315 21 3 19.6569 3 18V8Z" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 13H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 13H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 13H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M8 17H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 17H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const DateRangePicker = ({ 
  value, 
  onChange, 
  name, 
  id, 
  placeholder = 'Select date range', 
  className = '', 
  required = false,
  format = 'YYYY-MM-DD', // Default ISO format
  label,
  disabled = false,
  // New props for separate start/end dates
  startDateName,
  endDateName,
  startDateValue,
  endDateValue
}) => {
  // State for the calendar visibility
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  // State for current month being viewed
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // States for the selected dates (from the value prop)
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // State for the selection phase (selecting start or end date)
  const [selectionPhase, setSelectionPhase] = useState('start');
  
  // Ref for the calendar popup to handle outside clicks
  const calendarRef = useRef(null);
  
  // Ref for the input container to position the calendar
  const inputContainerRef = useRef(null);
  
  // State for calendar position
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  
  // Effect to position the calendar when it opens
  useEffect(() => {
    if (isCalendarOpen && inputContainerRef.current) {
      const rect = inputContainerRef.current.getBoundingClientRect();
      setCalendarPosition({
        top: rect.bottom + 8, // Position below input with 8px gap
        left: rect.left,
        maxWidth: rect.width // Match width if needed
      });
    }
  }, [isCalendarOpen]);
  
  // Effect to parse the initial value if any
  useEffect(() => {
    // If using separate start/end date values (new format for laycan)
    if (startDateValue !== undefined && endDateValue !== undefined) {
      try {
        if (startDateValue) {
          const parsedStartDate = parseInputDate(startDateValue);
          if (parsedStartDate && !isNaN(parsedStartDate.getTime())) {
            setStartDate(parsedStartDate);
          }
        } else {
          setStartDate(null);
        }
        
        if (endDateValue) {
          const parsedEndDate = parseInputDate(endDateValue);
          if (parsedEndDate && !isNaN(parsedEndDate.getTime())) {
            setEndDate(parsedEndDate);
          }
        } else {
          setEndDate(null);
        }
      } catch (error) {
        console.error("Error parsing separate date values:", error);
      }
    }
    // Otherwise use old format (single value string)
    else if (value) {
      try {
        // Try to split the value into start and end dates
        if (value.includes('to')) {
          const [startString, endString] = value.split('to').map(str => str.trim());
          const parsedStartDate = parseInputDate(startString);
          const parsedEndDate = parseInputDate(endString);
          
          if (parsedStartDate && !isNaN(parsedStartDate.getTime())) {
            setStartDate(parsedStartDate);
          }
          
          if (parsedEndDate && !isNaN(parsedEndDate.getTime())) {
            setEndDate(parsedEndDate);
          }
        } else {
          // If it's a single date, treat it as the start date
          const parsedDate = parseInputDate(value);
          if (parsedDate && !isNaN(parsedDate.getTime())) {
            setStartDate(parsedDate);
          }
        }
      } catch (error) {
        console.error("Error parsing date range:", error);
      }
    }
  }, [value, startDateValue, endDateValue]);
  
  // Effect to handle clicking outside the calendar to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };
    
    // Add event listener when calendar is open
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      
      // Reposition calendar on scroll or resize
      const handlePositionChange = () => {
        if (inputContainerRef.current) {
          const rect = inputContainerRef.current.getBoundingClientRect();
          setCalendarPosition({
            top: rect.bottom + 8,
            left: rect.left,
            maxWidth: rect.width
          });
        }
      };
      
      window.addEventListener('scroll', handlePositionChange);
      window.addEventListener('resize', handlePositionChange);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handlePositionChange);
        window.removeEventListener('resize', handlePositionChange);
      };
    }
    
    // Remove event listener when component unmounts or calendar closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);
  
  // Helper to parse the input date string
  const parseInputDate = (dateString) => {
    if (!dateString) return null;
    
    // Handle different date formats
    let dateParts;
    
    // Date with dashes
    if (dateString.includes('-')) {
      dateParts = dateString.split('-');
      // Check if first part is year (YYYY-MM-DD) or day (DD-MM-YYYY)
      if (dateParts[0].length === 4) {
        // ISO format (YYYY-MM-DD)
        return new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      } else {
        // DD-MM-YYYY format
        return new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
      }
    }
    
    // MM/DD/YYYY format
    if (dateString.includes('/')) {
      dateParts = dateString.split('/');
      // Assuming MM/DD/YYYY format
      return new Date(parseInt(dateParts[2]), parseInt(dateParts[0]) - 1, parseInt(dateParts[1]));
    }
    
    // Try direct parsing as fallback
    return new Date(dateString);
  };
  
  // Helper to format a single date for display (DD-MM-YYYY)
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  };
  
  // Helper to format a single date for backend (YYYY-MM-DD)
  const formatDate = (date) => {
    if (!date) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${year}-${month}-${day}`;
  };
  
  // Helper to format the date range for display in input (DD-MM-YYYY to DD-MM-YYYY)
  const formatDateRange = () => {
    if (!startDate) return '';
    
    const formattedStart = formatDateForDisplay(startDate);
    const formattedEnd = endDate ? formatDateForDisplay(endDate) : '';
    
    if (formattedEnd) {
      return `${formattedStart} to ${formattedEnd}`;
    }
    
    return formattedStart;
  };
  
  // Handler for date selection
  const handleDateSelect = (day, month = currentMonth.getMonth(), year = currentMonth.getFullYear()) => {
    const selectedDate = new Date(year, month, day);
    
    if (selectionPhase === 'start') {
      // First select start date, then prepare to select end date
      setStartDate(selectedDate);
      setEndDate(null);
      setSelectionPhase('end');
      
      // If using separate date fields, update start date immediately
      if (startDateName && onChange) {
        onChange({
          target: {
            name: startDateName,
            value: formatDate(selectedDate)
          }
        });
      }
    } else {
      // When selecting end date
      let finalStartDate = startDate;
      let finalEndDate = selectedDate;
      
      // Ensure end date is not before start date
      if (startDate && selectedDate < startDate) {
        // If selected date is before start date, swap them
        finalStartDate = selectedDate;
        finalEndDate = startDate;
        setEndDate(startDate);
        setStartDate(selectedDate);
      } else {
        setEndDate(selectedDate);
      }
      
      if (onChange) {
        // If using separate date fields (new format)
        if (startDateName && endDateName) {
          // Call onChange twice for both fields
          onChange({
            target: {
              name: startDateName,
              value: formatDate(finalStartDate)
            }
          });
          onChange({
            target: {
              name: endDateName,
              value: formatDate(finalEndDate)
            }
          });
        } 
        // Otherwise use old format (single concatenated value)
        else {
          const formattedRange = `${formatDate(finalStartDate)} to ${formatDate(finalEndDate)}`;
          onChange({
            target: {
              name,
              value: formattedRange
            }
          });
        }
      }
      
      // Reset selection phase and close calendar
      setSelectionPhase('start');
      setIsCalendarOpen(false);
    }
  };
  
  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  // Toggle calendar visibility
  const toggleCalendar = () => {
    if (!disabled) {
      setIsCalendarOpen(!isCalendarOpen);
      
      // If opening the calendar and we have a selected date, set the current month to that date
      if (!isCalendarOpen && startDate) {
        setCurrentMonth(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
      }
      
      // Always reset to selecting start date when opening calendar
      if (!isCalendarOpen) {
        setSelectionPhase('start');
      }
    }
  };
  
  // Handler for manual input in the text field
  const handleInputChange = (e) => {
    if (onChange) {
      onChange(e);
    }
    
    const inputValue = e.target.value;
    
    // Try to parse the manually entered date range
    try {
      if (inputValue.includes('to')) {
        const [startString, endString] = inputValue.split('to').map(str => str.trim());
        const parsedStartDate = parseInputDate(startString);
        const parsedEndDate = parseInputDate(endString);
        
        if (parsedStartDate && !isNaN(parsedStartDate.getTime())) {
          setStartDate(parsedStartDate);
        }
        
        if (parsedEndDate && !isNaN(parsedEndDate.getTime())) {
          setEndDate(parsedEndDate);
        }
      } else {
        // If it's a single date, treat it as the start date
        const parsedDate = parseInputDate(inputValue);
        if (parsedDate && !isNaN(parsedDate.getTime())) {
          setStartDate(parsedDate);
          setEndDate(null);
        }
      }
    } catch (error) {
      console.error("Error parsing manually entered date range:", error);
    }
  };
  
  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Initialize the calendar array with empty cells for the days before the first day of the month
    const calendarDays = Array(firstDayOfWeek).fill(null);
    
    // Add days of the month
    for (let day = 1; day <= totalDays; day++) {
      calendarDays.push(day);
    }
    
    return calendarDays;
  };
  
  // Month names for header
  const monthNames = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];
  
  // Day names for header
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  
  // Get the name of the current month and year
  const currentMonthName = monthNames[currentMonth.getMonth()];
  const currentYear = currentMonth.getFullYear();
  
  // Render the calendar days
  const calendarDays = generateCalendarDays();
  
  // Check if a day is part of the selected range
  const isDayInRange = (day) => {
    if (!day || !startDate) return false;
    
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    // If only start date is selected or we're in the process of selecting the end date
    if (!endDate || selectionPhase === 'end') {
      return currentDate.getTime() === startDate.getTime();
    }
    
    // If both dates are selected
    return currentDate >= startDate && currentDate <= endDate;
  };
  
  // Check if a day is the start of the range
  const isStartDay = (day) => {
    if (!day || !startDate) return false;
    
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return currentDate.getTime() === startDate.getTime();
  };
  
  // Check if a day is the end of the range
  const isEndDay = (day) => {
    if (!day || !endDate) return false;
    
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return currentDate.getTime() === endDate.getTime();
  };
  
  // Check if a day is today
  const isToday = (day) => {
    if (!day) return false;
    
    const today = new Date();
    return (
      today.getDate() === day && 
      today.getMonth() === currentMonth.getMonth() && 
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Get cell class based on its state
  const getCellClass = (day) => {
    if (!day) return 'empty-cell';
    
    let classes = [];
    
    if (isStartDay(day)) classes.push('range-start selected');
    if (isEndDay(day)) classes.push('range-end selected');
    if (!isStartDay(day) && !isEndDay(day) && isDayInRange(day)) classes.push('in-range');
    if (isToday(day)) classes.push('today');
    
    return classes.join(' ');
  };

  return (
    <div className={`invoice-date-picker-container ${className} ${isCalendarOpen ? 'calendar-open' : ''}`}>
      {label && <label htmlFor={id || name} className="invoice-date-picker-label">{label} {required && <span className="required-field">*</span>}</label>}
      
      <div className="invoice-date-picker-input-container" ref={inputContainerRef}>
        <input
          type="text"
          id={id || name}
          name={name}
          value={formatDateRange() || ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={`invoice-date-picker-input ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          required={required}
          readOnly={true}
          onClick={toggleCalendar}
        />
        
        <button 
          type="button" 
          onClick={toggleCalendar} 
          className={`invoice-date-picker-button ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          aria-label="Select date range"
        >
          <CalendarIcon />
        </button>
      </div>
      
      {isCalendarOpen && (
        <div 
          className="invoice-date-picker-calendar" 
          ref={calendarRef}
          style={{
            top: `${calendarPosition.top}px`,
            left: `${calendarPosition.left}px`,
            maxWidth: `${calendarPosition.maxWidth}px`
          }}
        >
          <div className="invoice-calendar-header">
            <button 
              type="button" 
              className="invoice-calendar-nav-button" 
              onClick={goToPreviousMonth}
              aria-label="Previous month"
            >
              <span className="invoice-calendar-nav-icon">&#10094;</span>
            </button>
            
            <div className="invoice-calendar-header-title">
              <span className="invoice-calendar-month-name">{currentMonthName} {currentYear}</span>
            </div>
            
            <button 
              type="button" 
              className="invoice-calendar-nav-button" 
              onClick={goToNextMonth}
              aria-label="Next month"
            >
              <span className="invoice-calendar-nav-icon">&#10095;</span>
            </button>
          </div>
          
          <div style={{ padding: '8px', textAlign: 'center', fontSize: '0.85rem', color: '#4a6b25', fontWeight: 500, borderBottom: '1px solid #f0f0f0', background: '#f9f9f9' }}>
            {selectionPhase === 'start' ? 'Select start date' : 'Select end date'}
          </div>
          
          <div className="invoice-calendar-days-header">
            {dayNames.map((day, index) => (
              <div key={index} className="invoice-calendar-day-name">{day}</div>
            ))}
          </div>
          
          <div className="invoice-calendar-days-grid">
            {calendarDays.map((day, index) => (
              <div 
                key={index} 
                className={`invoice-calendar-day-cell ${getCellClass(day)}`}
                onClick={() => day && handleDateSelect(day)}
              >
                {day}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker; 